import React from 'react';

import {
  RecordContextProvider,
  useRecordContext,
} from 'react-admin';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import ChevronRight from '@mui/icons-material/ChevronRight';

import { get } from 'lodash';

import FirstName from './FirstName';
import LastName from './LastName';
import TaxResidency from './TaxResidency';
import Pep from './Pep';
import Role from './Role';
import Citizenship from './Citizenship';
import Custom from './Custom';

import languages, { Languages } from '../../../../constants/languages';
import Answer from '../Answer';
import NationalIdentifier from './NationalIdentifier';
import DateOfBirth from './DateOfBirth';
import Address from './Address';
import PostalCode from './PostalCode';
import City from './City';
import Residence from './Residence';
import conflictFields from '../../../../constants/conflictFields';
import questions from '../../../../constants/questions';
import { SignatoriesParameters } from '../../questionParameters';

const LABEL = {
  [languages.EN]: 'Signatories',
  [languages.SV]: 'Firmatecknare',
  [languages.DE]: 'Zeichnungsberechtigte',
};

const ValueField = ({ language, extraParameters }: {
  language?: Languages;
  extraParameters?: SignatoriesParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value') as {firstName: string; lastName: string}[] | undefined;
  const sx = get(record, 'sx', {});

  if (!value) return null;

  return (
    <Card
      variant="outlined"
      square
      sx={{
        m: 0,
        py: 0,
        width: '100%',
      }}
    >
      <Grid container bgcolor="background.paper">
        {value?.map((signatory, index) => (
          <Grid
            key={`signatory-${index + 1}`}
            size={12}
          >
            <Accordion key={`${signatory.firstName}  ${signatory.lastName}`} sx={{ maxWidth: null, backgroundColor: 'transparent' }}>
              <AccordionSummary
                expandIcon={<ChevronRight />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                    ml: 4,
                    ...sx,
                  }}
                  variant="h6"
                >
                  {`${signatory.firstName} ${signatory.lastName}`}
                </Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <RecordContextProvider value={{ language, ...signatory, sx }}>
                  <Grid container spacing={6} py="1%" px="1%">
                    <FirstName.Nested extraParameters={extraParameters?.[questions.Q_FIRST_NAME]} />
                    <LastName.Nested extraParameters={extraParameters?.[questions.Q_LAST_NAME]} />
                    <Role.Nested extraParameters={extraParameters?.[questions.Q_ROLE]} />
                    <DateOfBirth.Nested
                      extraParameters={extraParameters?.[questions.Q_DATE_OF_BIRTH]}
                    />
                    <Address.Nested extraParameters={extraParameters?.[questions.Q_ADDRESS]} />
                    <PostalCode.Nested
                      extraParameters={extraParameters?.[questions.Q_POSTAL_CODE]}
                    />
                    <City.Nested extraParameters={extraParameters?.[questions.Q_CITY]} />
                    <Residence.Nested extraParameters={extraParameters?.[questions.Q_RESIDENCE]} />
                    <NationalIdentifier.Nested
                      extraParameters={extraParameters?.[questions.Q_NATIONAL_IDENTIFIER]}
                    />
                    <TaxResidency.Nested
                      extraParameters={extraParameters?.[questions.Q_TAX_RESIDENCY]}
                    />
                    <Citizenship.Nested
                      extraParameters={extraParameters?.[questions.Q_CITIZENSHIP]}
                    />
                    <Pep.Nested extraParameters={extraParameters?.[questions.Q_PEP]} />
                    {extraParameters?.customQuestions?.map((customQuestion) => (
                      <Custom.Nested key={customQuestion.id} extraParameters={customQuestion} />
                    ))}
                  </Grid>
                </RecordContextProvider>
              </AccordionDetails>
            </Accordion>
            {index + 1 !== value.length && <Divider />}
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

const Signatorires = ({ extraParameters }: {
  extraParameters?: SignatoriesParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.label ?? LABEL[language]}
      isComplexType
      source="signatories"
      datapoint={conflictFields.SIGNATORIES}
      identificationInfo={undefined}
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

Signatorires.ValueField = ValueField;

export default Signatorires;
